import { AgentFile, NexusAgent } from '../../../../domain/agents/models/Agent';
import { ChatProviderType } from '../../../../domain/chat/models/ChatProviderType';
import {
    FunctionToolDefinition,
    FunctionToolParameter,
    functionToolParameterTypes,
} from '../../../../domain/agents/models/FunctionTool';
import {
    NexusAgentDto,
    NexusFunctionParameterDto,
    NexusFunctionToolDefinitionDto,
} from '../dtos/responses/NexusAgentDto';
import { parseNexusAgentMetadata } from './parseNexusAgentMetadata';

const mapFunctionToolParameterDtoToModel = (parameter: NexusFunctionParameterDto): FunctionToolParameter => {
    const normalizedType = functionToolParameterTypes.includes(parameter.type as FunctionToolParameter['type'])
        ? (parameter.type as FunctionToolParameter['type'])
        : 'string';

    return {
        type: normalizedType,
        description: parameter.description,
        required: parameter.required,
        enum: parameter.enum,
    };
};

const mapFunctionToolDtoToModel = (functionTool: NexusFunctionToolDefinitionDto): FunctionToolDefinition => {
    const parameters = Object.entries(functionTool.parameters ?? {}).reduce<Record<string, FunctionToolParameter>>(
        (accumulator, [name, parameter]) => {
            accumulator[name] = mapFunctionToolParameterDtoToModel(parameter);
            return accumulator;
        },
        {},
    );

    return {
        name: functionTool.name,
        description: functionTool.description,
        strictMode: functionTool.strictMode,
        parameters,
    };
};

export const mapNexusAgentDtoToModel = (agent: NexusAgentDto): NexusAgent => {
    // Parse real metadata from the backend when available; fall back to mock data per-field.
    const metadata = agent.metadata ? parseNexusAgentMetadata(agent.metadata) : undefined;

    return {
        agentId: agent.id,
        providerType: ChatProviderType.Nexus,
        name: agent.name,
        description: agent.description ?? '',
        fileSearchDocuments: (agent.fileSearchDocuments ?? []).map<AgentFile>((f) => ({
            ...f,
        })),
        codeInterpreterDocuments: (agent.codeInterpreterDocuments ?? []).map<AgentFile>((f) => ({
            ...f,
        })),
        functionTools: agent.functionTools?.map(mapFunctionToolDtoToModel),
        agentIconUrl: metadata?.agentIconUrl ?? "",
        conversationStarters: metadata?.promptLibrary ?? [],
        shortDescription: metadata?.cardShortDescription,
        disclaimer: metadata?.disclaimer,
        relatedLinks: metadata?.relatedLinks,
        faqs: metadata?.faqs,
        knowledgeBase: metadata?.knowledgeBase,
        ownerBlurb: metadata?.ownerBlurb,
        supportAddress: metadata?.supportAddress,
        ownerInfo: metadata?.ownerInfo,
        capabilities: metadata?.capabilities,
        ownershipMode: metadata?.ownershipMode,
        instructions: agent.instructions,
        model: agent.model,
        enableFileSearch: agent.enableFileSearch,
        enableCodeInterpreter: agent.enableCodeInterpreter,
        allowFileSearchAtConversationLevel: metadata?.allowFileSearchAtConversationLevel ?? false,
        allowCodeInterpreterAtConversationLevel: metadata?.allowCodeInterpreterAtConversationLevel ?? false,
        oldAssistantId: metadata?.oldAssistantId,
        mcpServerIds: agent.mcpServerIds ?? [],
    };
};
